// Lake geometry for the 2D map: projection to Web Mercator world units once per setLakes(),
// then per-draw projection to screen with viewport culling and decimation. Pure, no DOM
// (the path sink is structurally compatible with Path2D / CanvasRenderingContext2D).

import type { LakeFile, LineCoords, MultiPolygonCoords } from '../contracts';
import { latToY, lonToX } from './mercator';

/** Flattened [x0, y0, x1, y1, ...] in world units plus its bbox. */
export interface WorldPath {
  pts: Float64Array;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface ProjectedSlipway {
  x: number;
  y: number;
  name: string | null;
}

export interface ProjectedLake {
  id: string;
  name: string;
  /** World bbox of everything drawn (coverage + zones + lines). */
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** All rings (exteriors and holes) flattened; fill with 'evenodd'. */
  inner: WorldPath[];
  outer: WorldPath[];
  /** Extended-zone polygons (beyond the outer/300 m limit), drawn as a distinct orange fill. */
  extended: WorldPath[];
  coverage: WorldPath[];
  /** Open polylines. */
  extendedLines: WorldPath[];
  /** Pier/jetty/notch outlines on the water (thin lines, not the finish band). */
  pierLines: WorldPath[];
  slipways: ProjectedSlipway[];
  /** OSM man_made=pier lines/points, drawn like pierLines/slipways but styled distinctly. */
  osmPierLines: WorldPath[];
  osmPierPoints: ProjectedSlipway[];
}

export interface PathSink {
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  closePath(): void;
}

/** Affine world -> screen transform: sx = wx * scale + ox, sy = wy * scale + oy (CSS px). */
export interface ScreenXform {
  scale: number;
  ox: number;
  oy: number;
}

/** Culling rectangle in screen CSS px (usually the viewport expanded by a margin). */
export interface CullRect {
  xmin: number;
  ymin: number;
  xmax: number;
  ymax: number;
}

function projectCoords(coords: [number, number][]): WorldPath {
  const pts = new Float64Array(coords.length * 2);
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (let i = 0; i < coords.length; i++) {
    const x = lonToX(coords[i][0]);
    const y = latToY(coords[i][1]);
    pts[2 * i] = x;
    pts[2 * i + 1] = y;
    if (x < x0) x0 = x;
    if (x > x1) x1 = x;
    if (y < y0) y0 = y;
    if (y > y1) y1 = y;
  }
  return { pts, x0, y0, x1, y1 };
}

function projectMulti(mp: MultiPolygonCoords): WorldPath[] {
  const out: WorldPath[] = [];
  for (const poly of mp) for (const ring of poly) if (ring.length >= 3) out.push(projectCoords(ring));
  return out;
}

function projectLines(lines: LineCoords[]): WorldPath[] {
  return lines.filter((l) => l.length >= 2).map(projectCoords);
}

export function projectLake(lake: LakeFile): ProjectedLake {
  const inner = projectMulti(lake.inner);
  const outer = projectMulti(lake.outer);
  const extended = projectMulti(lake.extended);
  const coverage = projectMulti(lake.coverage);
  const extendedLines = projectLines(lake.extendedLines);
  const pierLines = projectLines(lake.pierLines ?? []);
  const slipways: ProjectedSlipway[] = (lake.slipways ?? []).map((w) => ({
    x: lonToX(w.lon),
    y: latToY(w.lat),
    name: w.name,
  }));
  const osmPierLines = projectLines(lake.osmPierLines ?? []);
  const osmPierPoints: ProjectedSlipway[] = (lake.osmPierPoints ?? []).map((w) => ({
    x: lonToX(w.lon),
    y: latToY(w.lat),
    name: w.name,
  }));
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const p of [...inner, ...outer, ...extended, ...coverage, ...extendedLines, ...pierLines, ...osmPierLines]) {
    if (p.x0 < x0) x0 = p.x0;
    if (p.y0 < y0) y0 = p.y0;
    if (p.x1 > x1) x1 = p.x1;
    if (p.y1 > y1) y1 = p.y1;
  }
  for (const s of [...slipways, ...osmPierPoints]) {
    if (s.x < x0) x0 = s.x;
    if (s.x > x1) x1 = s.x;
    if (s.y < y0) y0 = s.y;
    if (s.y > y1) y1 = s.y;
  }
  return {
    id: lake.meta.id,
    name: lake.meta.name,
    x0,
    y0,
    x1,
    y1,
    inner,
    outer,
    extended,
    coverage,
    extendedLines,
    pierLines,
    slipways,
    osmPierLines,
    osmPierPoints,
  };
}

/** True if the world bbox, transformed to screen, intersects the cull rect. */
export function bboxVisible(x0: number, y0: number, x1: number, y1: number, t: ScreenXform, r: CullRect): boolean {
  return (
    x1 * t.scale + t.ox >= r.xmin &&
    x0 * t.scale + t.ox <= r.xmax &&
    y1 * t.scale + t.oy >= r.ymin &&
    y0 * t.scale + t.oy <= r.ymax
  );
}

/**
 * Append a ring or polyline to the sink in screen coordinates, culled and decimated.
 *
 * Culling is exact for fills (any fill rule) inside the cull rect: a run of consecutive vertices that
 * all lie in the same outside half-plane (left of xmin, right of xmax, above ymin or below ymax) is
 * replaced by its first and last vertex. The removed detour and its chord both lie in that half-plane,
 * so winding numbers of points inside the rect are unchanged. Inside the rect, vertices closer than
 * minStep (px, per axis) to the previously emitted vertex are dropped.
 *
 * Returns the number of emitted vertices (0 if the path was skipped entirely).
 */
export function appendCulled(
  sink: PathSink,
  path: WorldPath,
  closed: boolean,
  t: ScreenXform,
  r: CullRect,
  minStep: number,
): number {
  // Whole-path rejection; for fills this is exact under 'evenodd' and 'nonzero' alike.
  if (!bboxVisible(path.x0, path.y0, path.x1, path.y1, t, r)) return 0;
  // Sub-pixel rings are invisible.
  if (closed && (path.x1 - path.x0) * t.scale < minStep && (path.y1 - path.y0) * t.scale < minStep) return 0;

  const pts = path.pts;
  const n = pts.length >> 1;
  const { scale, ox, oy } = t;
  const { xmin, ymin, xmax, ymax } = r;
  let emitted = 0;
  let lastX = 0;
  let lastY = 0;
  let runMask = 0;
  let runHasLast = false;
  let runLastX = 0;
  let runLastY = 0;

  for (let i = 0; i < n; i++) {
    const x = pts[2 * i] * scale + ox;
    const y = pts[2 * i + 1] * scale + oy;
    const code = (x < xmin ? 1 : 0) | (x > xmax ? 2 : 0) | (y < ymin ? 4 : 0) | (y > ymax ? 8 : 0);
    if (runMask !== 0) {
      const m = runMask & code;
      if (m !== 0) {
        runMask = m;
        runLastX = x;
        runLastY = y;
        runHasLast = true;
        continue;
      }
      if (runHasLast) {
        sink.lineTo(runLastX, runLastY);
        emitted++;
      }
      runMask = 0;
      runHasLast = false;
    }
    if (code !== 0) {
      // First vertex of an outside run: always emitted.
      if (emitted === 0) sink.moveTo(x, y);
      else sink.lineTo(x, y);
      emitted++;
      lastX = x;
      lastY = y;
      runMask = code;
      continue;
    }
    if (emitted === 0) {
      sink.moveTo(x, y);
    } else {
      if (i < n - 1 && Math.abs(x - lastX) < minStep && Math.abs(y - lastY) < minStep) continue;
      sink.lineTo(x, y);
    }
    emitted++;
    lastX = x;
    lastY = y;
  }
  if (runHasLast) {
    sink.lineTo(runLastX, runLastY);
    emitted++;
  }
  if (closed && emitted > 0) sink.closePath();
  return emitted;
}

/**
 * Black squares of a two-row checkered "finish line" along a polyline, in screen space with a
 * constant square size `cell` (CSS px). Rows lie on both sides of the line: square k (arc length
 * [k*cell, (k+1)*cell)) is black on the left row for even k and on the right row for odd k, so the
 * pattern alternates along and across the line. The white background band (width 2*cell) is drawn
 * separately as a stroke. Squares spanning a vertex are split per segment. Only the part of each
 * segment inside the cull rect is processed (Liang-Barsky), but the arc length phase is continuous.
 *
 * Returns the number of quads emitted.
 */
export function appendFinishSquares(sink: PathSink, path: WorldPath, t: ScreenXform, r: CullRect, cell: number): number {
  if (!bboxVisible(path.x0, path.y0, path.x1, path.y1, t, r)) return 0;
  const pts = path.pts;
  const { scale, ox, oy } = t;
  let quads = 0;
  let s = 0;
  let px = pts[0] * scale + ox;
  let py = pts[1] * scale + oy;
  for (let i = 2; i < pts.length; i += 2) {
    const qx = pts[i] * scale + ox;
    const qy = pts[i + 1] * scale + oy;
    const dx = qx - px;
    const dy = qy - py;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len > 1e-9) {
      const clip = clipSegment(px, py, dx, dy, r);
      if (clip !== null) {
        const ux = dx / len;
        const uy = dy / len;
        // Left normal in screen space (y down): rotate u by -90 deg.
        const nx = uy;
        const ny = -ux;
        const a = clip[0] * len;
        const b = clip[1] * len;
        const sa = s + a;
        const sb = s + b;
        for (let k = Math.floor(sa / cell); k * cell < sb; k++) {
          const c0 = Math.max(sa, k * cell) - s;
          const c1 = Math.min(sb, (k + 1) * cell) - s;
          if (c1 - c0 < 1e-6) continue;
          const o = (k & 1) === 0 ? cell : -cell;
          const ax = px + ux * c0;
          const ay = py + uy * c0;
          const bx = px + ux * c1;
          const by = py + uy * c1;
          sink.moveTo(ax, ay);
          sink.lineTo(bx, by);
          sink.lineTo(bx + nx * o, by + ny * o);
          sink.lineTo(ax + nx * o, ay + ny * o);
          sink.closePath();
          quads++;
        }
      }
    }
    s += len;
    px = qx;
    py = qy;
  }
  return quads;
}

// Reused result tuple for clipSegment (no per-segment allocation).
const clipOut: [number, number] = [0, 1];

/** Liang-Barsky: parameter range [t0, t1] of p + t*d inside r, or null. */
function clipSegment(px: number, py: number, dx: number, dy: number, r: CullRect): [number, number] | null {
  let t0 = 0;
  let t1 = 1;
  const p = [-dx, dx, -dy, dy];
  const q = [px - r.xmin, r.xmax - px, py - r.ymin, r.ymax - py];
  for (let k = 0; k < 4; k++) {
    if (p[k] === 0) {
      if (q[k] < 0) return null;
    } else {
      const u = q[k] / p[k];
      if (p[k] < 0) {
        if (u > t1) return null;
        if (u > t0) t0 = u;
      } else {
        if (u < t0) return null;
        if (u < t1) t1 = u;
      }
    }
  }
  clipOut[0] = t0;
  clipOut[1] = t1;
  return clipOut;
}
